import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignItemModel, CampaignItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignItems']

type CampaignItemFilters = {
  campaign_id?: number,
  string?: string,
}

type UseCampaignItemsOptions = {
  filters?: CampaignItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignItemRequestOptions,
}

function useCampaignItems(options: UseCampaignItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { campaignItems: campaignItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignItems } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignItems = useMemo(() => {
    const filtered = Object.values(campaignItems).filter((campaignItem: CampaignItemModel) => {
      const { campaign_id, id, title } = campaignItem

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchCampaign = matchFilterNumber(campaign_id, filterCampaignId)

      return matchCampaign && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [campaignItemsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignItemsCount = filteredCampaignItems.length
  const hasFilteredCampaignItems = !!filteredCampaignItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignItems } = useReduxAction(
    'campaignItems',
    'loadCampaignItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignItems,
    filteredCampaignItemsCount,
    hasFilteredCampaignItems,
    loading: loadingCampaignItems,
  }
}

export default useCampaignItems
